import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { IrrigationOperation } from "@/types/operations";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import {
    object,
    date,
    number,
    string,
    InferType,
} from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import DateInput from "@/components/inputs/DateInput";
import NumberInputComponent from "@/components/inputs/NumberInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import { Skeleton, Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "@tanstack/react-router";
import { roundTo } from "@/lib/utils/numbers";

const schema = object().shape({
    start_date: date().required(),
    depth: number().required().min(0),
    note: string().nullable(),
});

export type IrrigationOperationFormData = InferType<typeof schema>;

function IrrigationOperationForm({
    existingIrrigationId,
}: {
    existingIrrigationId?: number;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const existingIrrigationQuery = useGetRequest<IrrigationOperation>({
        url: `irrigation_operations/${existingIrrigationId}/`,
        enabled: !!existingIrrigationId,
    });
    const form = useForm<IrrigationOperationFormData>({
        resolver: yupResolver(schema),
    });
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const { fieldId, fieldQuery } = useFieldDashboard() ?? {};
    const irrigationSystem = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems.length
            ? fieldQuery?.data.irrigation_systems[0]
            : null;
    }, [fieldQuery?.data]);


    useEffect(() => {
        if (existingIrrigationQuery.data) {
            form.reset({
                start_date: dayjs(existingIrrigationQuery.data.start_date).toDate(),
                depth: roundTo(existingIrrigationQuery.data.average_depth, 2),
                note: existingIrrigationQuery.data.note,
            });
        } else {
            form.reset({
                start_date: new Date(),
                depth: undefined,
                note: undefined,
            })
        }
    }, [
        existingIrrigationQuery.data,
        form,
    ]);

    const saveOperationRequest = useMutateRequest({
        errorMessage: t("error.failed_to_upload_operation"),
        successMessage: t("success.operation_uploaded_successfully"),
    });

    const saveOperation = (data: IrrigationOperationFormData) => {
        const payload = {
            id: existingIrrigationId,
            field: fieldId,
            start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
            field_rate: { rate: data.depth },
            note: data.note === "" ? null : data.note,
            irrigation_system: irrigationSystem?.id || null,
        };
        const method = existingIrrigationId ? "PUT" : "POST";
        saveOperationRequest.mutate({
            url: existingIrrigationId
                ? `irrigation_operations/${existingIrrigationId}/`
                : "irrigation_operations/",
            method,
            data: payload,
        },
            {
                onSuccess: () => {
                    navigate({
                        to: "/dashboard/fields/$fieldId/operations",
                        params: {
                            fieldId: fieldId as string,
                        },
                    });
                },
            });
    }

    return existingIrrigationQuery.isLoading ? (
        <Skeleton className="h-40 w-full rounded-lg" />
    ) : (
        <FormProvider {...form}>
            <form>
                <div className="flex flex-col gap-2 text-sm">
                    <DateInput
                        name="start_date"
                        label={t("date")}
                        variant="flat"
                        isRequired
                        className="w-full"
                        size="sm"
                        labelPlacement="outside"
                        showMonthAndYearPickers
                    />
                    <NumberInputComponent
                        name="depth"
                        label={`${t("depth")} (${formatUnit("in", "cm")})`}
                        variant="flat"
                        size="sm"
                        placeholder={t("depth")}
                        className="w-full"
                        min={0}
                        step={0.1}
                        isRequired
                        labelPlacement="outside"
                    />
                    <TextAreaInput
                        className="flex-1"
                        name="note"
                        label={t("note")}
                        variant="flat"
                        size="sm"
                        placeholder={t("note")}
                        labelPlacement="outside"
                    />
                    <Button
                        fullWidth
                        color="secondary"
                        onPress={() => {
                            form.trigger();
                            form.handleSubmit(saveOperation)();
                        }}
                        isLoading={saveOperationRequest.isPending}
                    >
                        <FontAwesomeIcon
                            icon={
                                existingIrrigationId
                                    ? faSave
                                    : faPlusCircle
                            }
                            className="mr-2"
                        />
                        {existingIrrigationId
                            ? t("saveOperation")
                            : t("addOperation")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}
export default IrrigationOperationForm;